import { useEffect, useRef } from 'react';

/**
 * Combine multiple react refs into a single ref
 *
 * See: https://www.davedrinks.coffee/how-do-i-use-two-react-refs/
 */
export function mergeRefs<T>(...refs: Array<React.ForwardedRef<T>>) {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 1) return filteredRefs[0];

  return (inst: T) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else {
        ref.current = inst;
      }
    }
  };
}

/**
 * React hook to combine refs into a RefObject.
 *
 * See: https://itnext.io/reusing-the-ref-from-forwardref-with-react-hooks-4ce9df693dd
 */
export function useMergedRefs<T>(...refs: Array<React.ForwardedRef<T> | undefined>) {
  const targetRef = useRef<T>(null);

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
}
